import { useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { IconAlertCircle, IconCheck, IconMusic } from '@tabler/icons-react';

import { motion } from 'framer-motion';

import { PRIMARY_FONT } from '../../constants/fonts';
import { MusicService } from '../../services/musicService';

interface SimpleMusicSuggestion {
  id?: string;
  artist: string;
  songTitle: string;
  timestamp?: any;
}

interface MusicSuggestionFormProps {
  onSubmitSuccess?: () => void;
}

export function MusicSuggestionForm({ onSubmitSuccess }: MusicSuggestionFormProps) {
  const theme = useMantineTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [suggestions, setSuggestions] = useState<SimpleMusicSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);

  const form = useForm<SimpleMusicSuggestion>({
    initialValues: {
      artist: '',
      songTitle: '',
    },
    validate: {
      artist: value => (value.trim().length < 1 ? 'Wykonawca jest wymagany' : null),
      songTitle: value => (value.trim().length < 1 ? 'Tytuł utworu jest wymagany' : null),
    },
  });

  // Load existing suggestions
  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    try {
      setIsLoadingSuggestions(true);
      const data = await MusicService.getAllSuggestions();
      setSuggestions(data || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleSubmit = async (values: SimpleMusicSuggestion) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await MusicService.submitSuggestion({
        guestName: 'Gość',
        guestEmail: '',
        songTitle: values.songTitle.trim(),
        artist: values.artist.trim(),
        genre: '',
        specialRequest: '',
        isSpecialSong: false,
        specialSongType: '',
      });

      setSubmitStatus('success');
      form.reset();
      onSubmitSuccess?.();

      // Reload suggestions to show the new one
      await loadSuggestions();

      // Reset success message after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      console.error('Error submitting music suggestion:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container size="md">
      <Stack gap="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          viewport={{ once: true }}
        >
          <Stack align="center" gap="lg" mb={40}>
          <Badge
              size="lg"
              variant="light"
              color={theme.other.titleColor}
              style={{
                fontSize: '0.9rem',
                padding: '8px 16px',
                fontWeight: 500,
              }}
            >
              Muzyka na weselu
            </Badge>

            <Title
              order={2}
              ta="center"
              style={{
                fontFamily: PRIMARY_FONT,
                fontSize: 'clamp(2.5rem, 6vw, 4rem)',
                fontWeight: 700,
                color: theme.other.titleColor,
                lineHeight: 1.2,
                marginBottom: '1rem',
              }}
            >
              Propozycje muzyczne
            </Title>

            <Text
              size="lg"
              ta="center"
              c="dimmed"
              maw={600}
              style={{
                fontSize: '1.1rem',
                lineHeight: 1.6,
                fontFamily: PRIMARY_FONT,
              }}
            >
              Zaproponuj utwory, które chciałbyś usłyszeć podczas naszego wesela
            </Text>
          </Stack>
        </motion.div>

        {/* Add New Suggestion Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          viewport={{ once: true }}
        >
          <Paper
            p="xl"
            radius="20"
            style={{
              position: 'relative',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <LoadingOverlay visible={isSubmitting} />

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="lg">
                {submitStatus === 'success' && (
                  <Alert
                    icon={<IconCheck size={16} />}
                    title="Dziękujemy!"
                    color="green"
                    variant="light"
                  >
                    Twoja propozycja została dodana do listy.
                  </Alert>
                )}

                {submitStatus === 'error' && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Ups!"
                    color="red"
                    variant="light"
                  >
                    Wystąpił problem z dodaniem propozycji. Spróbuj ponownie.
                  </Alert>
                )}

                <Group grow>
                  <TextInput
                    label="Wykonawca"
                    placeholder="np. Ed Sheeran"
                    required
                    styles={{
                      label: {
                        fontFamily: PRIMARY_FONT,
                        fontWeight: 500,
                        color: '#1a1a1a',
                        marginBottom: '8px',
                      },
                      input: {
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        fontSize: '1rem',
                        padding: '12px 16px',
                        '&:focus': {
                          borderColor: theme.other.titleColor,
                          boxShadow: `0 0 0 3px rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.1)`,
                        },
                      },
                    }}
                    {...form.getInputProps('artist')}
                  />
                  <TextInput
                    label="Tytuł utworu"
                    placeholder="np. Perfect"
                    required
                    styles={{
                      label: {
                        fontFamily: PRIMARY_FONT,
                        fontWeight: 500,
                        color: '#1a1a1a',
                        marginBottom: '8px',
                      },
                      input: {
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        fontSize: '1rem',
                        padding: '12px 16px',
                        '&:focus': {
                          borderColor: theme.other.titleColor,
                          boxShadow: `0 0 0 3px rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.1)`,
                        },
                      },
                    }}
                    {...form.getInputProps('songTitle')}
                  />
                </Group>

                <Group justify="center" mt="lg">
                  <Button
                    type="submit"
                    size="lg"
                    loading={isSubmitting}
                    leftSection={<IconMusic size={18} />}
                    style={{
                      backgroundColor: theme.other.titleColor,
                      border: 'none',
                      borderRadius: '12px',
                      padding: '12px 32px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      fontFamily: PRIMARY_FONT,
                      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': {
                        backgroundColor: `rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.8)`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.3)`,
                      },
                    }}
                  >
                    {isSubmitting ? 'Dodawanie...' : 'Dodaj propozycję'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        </motion.div>

        {/* Suggestions List */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          viewport={{ once: true }}
        >
          <Paper
            p="xl"
            radius="20"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Stack gap="lg">
              <Stack align="center" gap="sm">
                                  <Box
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: theme.other.titleColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 15px rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.3)`,
                    }}
                  >
                    <IconMusic size={20} color="white" />
                  </Box>

                <Text
                  fw={600}
                  size="xl"
                  ta="center"
                  style={{
                    fontFamily: PRIMARY_FONT,
                    fontSize: '1.3rem',
                    color: 'black',
                  }}
                >
                  Wszystkie propozycje ({suggestions.length})
                </Text>
              </Stack>

              <Box
                style={{
                  height: '1px',
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
                }}
              />

              {isLoadingSuggestions ? (
                <Text
                  ta="center"
                  c="dimmed"
                  style={{
                    fontFamily: PRIMARY_FONT,
                    fontSize: '1rem',
                  }}
                >
                  Ładowanie propozycji...
                </Text>
              ) : suggestions.length === 0 ? (
                <Text
                  ta="center"
                  c="dimmed"
                  style={{
                    fontFamily: PRIMARY_FONT,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  }}
                >
                  Brak propozycji. Bądź pierwszy i dodaj swój ulubiony utwór!
                </Text>
              ) : (
                <Stack gap="sm">
                  {suggestions.map((suggestion, index) => (
                    <motion.div
                      key={suggestion.id || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.05,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                    >
                      <Card
                        p="lg"
                        radius="16"
                        style={{
                          background: 'rgba(255, 255, 255, 0.7)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(255, 255, 255, 0.4)',
                          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          cursor: 'default',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                          },
                        }}
                      >
                        <Group align="center" gap="md">
                          <Box
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: '8px',
                              background: theme.other.titleColor,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <IconMusic size={18} color="white" />
                          </Box>

                          <Box style={{ flex: 1 }}>
                            <Text
                              fw={600}
                              size="md"
                              style={{
                                fontFamily: PRIMARY_FONT,
                                color: '#1a1a1a',
                                marginBottom: '2px',
                              }}
                            >
                              {suggestion.songTitle}
                            </Text>
                            <Text
                              size="sm"
                              c="dimmed"
                              style={{
                                fontFamily: PRIMARY_FONT,
                              }}
                            >
                              {suggestion.artist}
                            </Text>
                          </Box>
                        </Group>
                      </Card>
                    </motion.div>
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
        </motion.div>
      </Stack>
    </Container>
  );
}
